import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';

/**
 * LoginRequired - Shows when user tries to access protected content without authentication
 * Replaces the legacy dashboard for unauthenticated users
 */
const LoginRequired = () => {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  const handleLogin = async () => {
    try {
      await loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/callback`,
        }
      });
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  // If user is already authenticated, they shouldn't see this page
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Logo/Icon */}
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          {/* Heading */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Authentication Required
          </h1>
          
          <p className="text-gray-600 mb-8">
            Please sign in to access your ArcAttend dashboard and manage your school's attendance.
          </p>

          {/* Login Button */}
          <button
            onClick={handleLogin}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 mb-4"
          >
            Sign In to Continue
          </button>

          {/* Alternative Actions */}
          <div className="space-y-3">
            <div className="text-sm text-gray-500">
              Don't have an account?
            </div>
            
            <Link 
              to="/register" 
              className="inline-block text-blue-600 hover:text-blue-700 font-medium"
            >
              Create an Account
            </Link>
            
            <div className="pt-4 border-t border-gray-200">
              <Link 
                to="/" 
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-500">
          Need help? Contact your school administrator or{' '}
          <button 
            onClick={() => window.open('mailto:team@arczen.com', '_blank')}
            className="text-blue-600 hover:text-blue-700 underline bg-transparent border-none cursor-pointer"
          >
            contact support
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginRequired;
